import {initializeApp , setLogLevel} from "firebase/app";
import {getToken, getMessaging, onMessage, isSupported} from "@firebase/messaging";
import {toast} from "vue3-toastify";
import {h,  ref} from "vue";
import PushNotification from "../components/toasts/PushNotification.vue";

const firebaseConfig = {
    apiKey: window.appConfig.FIREBASE_API_KEY,
    authDomain: window.appConfig.FIREBASE_AUTH_DOMAIN,
    projectId: window.appConfig.FIREBASE_PROJECT_ID,
    storageBucket: window.appConfig.FIREBASE_STORAGE_BUCKET,
    messagingSenderId: window.appConfig.FIREBASE_MESSAGING_SENDER_ID,
    appId: window.appConfig.FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

setLogLevel('debug');

let messaging = null;

const messagingSupported = isSupported().catch(() => false);

messagingSupported.then((supported) => {
    if (!supported) {
        console.log('Firebase messaging is not supported in this browser.');
        return;
    }

    messaging = getMessaging(app);
    onMessage(messaging, handleForegroundMessage);
}).catch((error) => {
    messaging = null;
    console.warn('Firebase messaging initialization failed.', error);
});

export const notificationState = {
    allMembersWiped: ref(false),
    allMembersImported: ref(false),
    toBeImported: ref(0),
    alreadyImported: ref(0),
};

const FIREBASE_SW_PATH = '/firebase-messaging-sw.js';
const FIREBASE_SW_SCOPE = '/firebase-cloud-messaging-push-scope';

function waitForActiveServiceWorker(registration) {
    const worker = registration.installing ?? registration.waiting;

    if (registration.active || !worker) {
        return Promise.resolve(registration);
    }

    return new Promise((resolve) => {
        worker.addEventListener('statechange', () => {
            if (worker.state === 'activated' || worker.state === 'redundant') {
                resolve(registration);
            }
        });
    });
}

const getActiveServiceWorkerRegistration = async () => {
    const registration = await navigator.serviceWorker.register(FIREBASE_SW_PATH, {scope: FIREBASE_SW_SCOPE});

    registration.update().catch(() => {});

    return waitForActiveServiceWorker(registration);
}

const getFirebaseTokenOrRequestPermission = async () => {
    if (!(await messagingSupported) || !messaging) {
        return null;
    }

    return getActiveServiceWorkerRegistration()
        .then((serviceWorkerRegistration) => getToken(messaging, {
            vapidKey: window.appConfig.FIREBASE_VAPID_KEY,
            serviceWorkerRegistration,
        }))
        .then((token) => {
            return Promise.resolve(token);
        })
        .catch((err) => {
            if (err?.code === 'messaging/permission-blocked') {
                console.log('Notification permission denied by user.');
                return Promise.resolve(null);
            }

            if (typeof Notification !== 'undefined') {
                Notification.requestPermission()
                    .then((permission) => {
                        if (permission === 'granted') {
                            console.log('Notification permission granted.');
                        } else {
                            console.log('Notification permission denied.');
                        }
                    });
            }

            console.warn('Unable to retrieve firebase messaging token.', err);

            return Promise.resolve(null);
        });
}

let lastToastPercent = 0;
const TOAST_PERCENT_THRESHOLD = 25; // Show toast every 25%

function handleForegroundMessage(payload) {
    if(Object.prototype.hasOwnProperty.call(payload.data, 'already-wiped') && Object.prototype.hasOwnProperty.call(payload.data, 'to-be-wiped')) {
        const alreadyWiped = payload.data['already-wiped'];
        const toBeWiped = payload.data['to-be-wiped'];

        if (alreadyWiped === toBeWiped) {
            notificationState.allMembersWiped.value = true;
        }
    } else if(Object.prototype.hasOwnProperty.call(payload.data, 'already-imported') && Object.prototype.hasOwnProperty.call(payload.data, 'to-be-imported')) {
        const alreadyImported = Number(payload.data['already-imported']);
        const toBeImported = Number(payload.data['to-be-imported']);
        const currentPercent = Math.floor((alreadyImported / toBeImported) * 100);

        notificationState.alreadyImported.value = alreadyImported;
        notificationState.toBeImported.value = toBeImported;

        const shouldShowToast =
            currentPercent >= lastToastPercent + TOAST_PERCENT_THRESHOLD ||
            alreadyImported === toBeImported || alreadyImported === 1;

        if (shouldShowToast && !notificationState.allMembersImported.value) {
            lastToastPercent = currentPercent;

            let options = {
                autoClose: Number(payload.data.duration) ?? false,
            };

            toast(h(PushNotification, {
                title: payload.notification.title,
                body: payload.notification.body,
                go_to: payload.data.go_to ?? null,
                timestamp: payload.data.timestamp ?? null,
            }), options);
        }

        if (alreadyImported === toBeImported) {
            notificationState.allMembersImported.value = true;
            lastToastPercent = 0;
        }
    } else {
        showBrowserNotification(payload.notification.title, {
            body: payload.notification.body,
            vibrate: true
        });
    }
}

function showBrowserNotification(title, options) {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
        return;
    }

    // Mobile browsers (e.g. Android Chrome) forbid the Notification constructor
    // and throw "Illegal constructor", requiring the active service worker
    // registration instead. Prefer it when available and fall back to the
    // constructor (guarded) for desktop browsers that still support it.
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistration()
            .then((registration) => {
                if (registration) {
                    registration.showNotification(title, options);
                } else {
                    constructNotification(title, options);
                }
            })
            .catch(() => constructNotification(title, options));

        return;
    }

    constructNotification(title, options);
}

function constructNotification(title, options) {
    try {
        new Notification(title, options);
    } catch (error) {
        console.warn('Unable to display notification.', error);
    }
}

export {getFirebaseTokenOrRequestPermission, messaging};
